import * as DialogPrimitive from "@radix-ui/react-dialog";
import clsx from "clsx";
import React from "react";
import { Button } from "../button/Button";
import { IconButton } from "../iconButton/IconButton";
import * as styles from "./Dialog.css";
import { useDialogFeedback } from "./DialogFeedbackContext";

type DialogSize = "sm" | "md" | "lg" | "xl" | "full" | "auto";

export interface DialogProps {
  /**
   * Dialog content
   */
  children?: React.ReactNode;
  /**
   * Dialog title
   */
  title?: string;
  /**
   * Dialog description
   */
  description?: string;

  /**
   * Confirm button text
   */
  confirmText?: string;
  /**
   * Cancel button text
   */
  cancelText?: string;
  /**
   * Dialog size
   */
  size?: DialogSize;
  /**
   * Extra class applied to the content surface, e.g. to override its
   * background. Composed after the base content styles so it can win.
   */
  contentClassName?: string;
  /**
   * Hide close icon
   */
  hideCloseIcon?: boolean;
  /**
   * Stable machine-readable key this dialog's feedback reports are grouped
   * by, e.g. ``"item-modal"``. Prefer it over ``title``, which is often
   * dynamic.
   */
  feedbackName?: string;
  /**
   * This dialog's own feedback capture block, read at submit time. Return
   * ids, tab names, counts and booleans — never row payloads.
   */
  feedbackContext?: () => Record<string, unknown>;
  /**
   * Hide the feedback icon, and with it this dialog's capture block. Use on
   * confirmation dialogs, where there is nothing to report.
   */
  hideFeedbackIcon?: boolean;
  /**
   * If true, renders the dialog as a modal with an overlay.
   * If false, displays the dialog inline next to its trigger without an overlay.
   */
  modal?: boolean;
  /**
   * Controlled open state
   * */
  open?: boolean;
  /**
   * Show loading spinner on confirm button
   */
  isLoading?: boolean;
  /**
   * Disable the confirm button without triggering the loading spinner.
   * Use to gate confirmation on caller-owned form validity (``isLoading``
   * remains the correct signal while a mutation is in flight).
   */
  confirmDisabled?: boolean;
  /**
   * Confirm handler
   */
  onConfirm?: () => void | boolean | Promise<void | boolean>;
  /**
   * Renders body and footer inside a ``<form>`` and makes the confirm button
   * its submit button, so its click and Enter in a field both land here.
   * The handler owns validation and closing; ``onConfirm`` is not called.
   * The default (navigation) is already prevented.
   */
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  /**
   * Open state change handler
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Close handler
   */
  onClose?: () => void;
}

export function Dialog({
  title,
  description,
  children,
  confirmText,
  cancelText,
  isLoading = false,
  confirmDisabled = false,
  size = "md",
  contentClassName,
  hideCloseIcon,
  feedbackName,
  feedbackContext,
  hideFeedbackIcon,
  modal = true,
  open,
  onConfirm,
  onSubmit,
  onOpenChange,
  onClose
}: DialogProps) {
  const feedback = useDialogFeedback();
  const showFeedbackIcon = !hideFeedbackIcon && feedback != null;
  // Skip the wrapper entirely when it would be empty, so a chrome-less
  // dialog keeps its title row's original single-child spacing.
  const showTitleActions = showFeedbackIcon || !hideCloseIcon;

  async function handleConfirmClick() {
    const result = onConfirm ? await onConfirm() : undefined;
    if (result !== false && onOpenChange) onOpenChange(false);
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit?.(event);
  }

  const bodyAndFooter = (
    <>
      {children && <div className={styles.body}>{children}</div>}

      {(cancelText || confirmText) && (
        <footer className={styles.footer}>
          {cancelText && (
            <DialogPrimitive.Close asChild>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                {cancelText}
              </Button>
            </DialogPrimitive.Close>
          )}
          {confirmText && (
            <Button
              {...(onSubmit
                ? { type: "submit" }
                : { type: "button", onClick: handleConfirmClick })}
              variant="primary"
              isLoading={isLoading}
              disabled={isLoading || confirmDisabled}
            >
              {confirmText}
            </Button>
          )}
        </footer>
      )}
    </>
  );

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={styles.overlay} />
        <DialogPrimitive.Content
          {...(!description && { "aria-describedby": undefined })}
          className={clsx(
            modal && styles.modalOverlay,
            styles.baseContent,
            styles.content[size],
            contentClassName
          )}
          onInteractOutside={(e) => {
            if (isLoading) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (isLoading) e.preventDefault();
          }}
        >
          {/* Header */}

          <header className={styles.header}>
            <div className={styles.titleRow}>
              <DialogPrimitive.Title className={clsx(styles.title)}>
                {title ?? ""}
              </DialogPrimitive.Title>

              {showTitleActions && (
                <div className={styles.titleActions}>
                  {/* Stays enabled while `isLoading`: it opens a dialog on
                      top rather than dismissing this one, and a stuck
                      submit is worth reporting. */}
                  {showFeedbackIcon && (
                    <feedback.DialogFeedback
                      name={feedbackName}
                      title={title}
                      size={size}
                      getContext={feedbackContext}
                    />
                  )}

                  {!hideCloseIcon && (
                    <DialogPrimitive.Close asChild disabled={isLoading}>
                      <IconButton
                        iconName="close"
                        variant="secondary"
                        size="md"
                        aria-label="Close"
                        onClick={onClose}
                        disabled={isLoading}
                      />
                    </DialogPrimitive.Close>
                  )}
                </div>
              )}
            </div>
            {description && (
              <DialogPrimitive.Description className={styles.description}>
                {description}
              </DialogPrimitive.Description>
            )}
          </header>

          {/* Body + footer: one form when the confirm button submits. */}
          {onSubmit ? (
            <form className={styles.form} onSubmit={handleSubmit}>
              {bodyAndFooter}
            </form>
          ) : (
            bodyAndFooter
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
